import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LoadingOverlayComponent, LoadingOverlayMethod, ModalBase } from 'morgana';
import { DialogComponent, DialogModule } from '@syncfusion/ej2-angular-popups';
import { PatientDocumentResponse } from '@gandalf/model/patient-document-response';
import { Observable } from 'rxjs';
import { MajButtonComponent, MajRichTextEditorComponent } from 'majora';
import { ToasterService } from '@core/toaster/toaster.service';
import { PatientDocumentContentRequest } from '@gandalf/model/patient-document-content-request';
import { FileService } from '@core/file/file.service';
import { TaskService } from '@core/task/task.service';
import { LetterEditorDockableModalService } from '@shared/component/letter-editor-dockable-modal/letter-editor-dockable-modal.service';

export interface LetterEditorDockableModalData {
	patientDocumentId: number;
	existingTask: boolean;
}

@Component({
	selector: 'pms-letter-editor-dockable-modal',
	templateUrl: './letter-editor-dockable-modal.component.html',
	styles: [],
	imports: [
		MajButtonComponent,
		LoadingOverlayComponent,
		DialogModule,
		MajRichTextEditorComponent,
		ReactiveFormsModule],
})
export class LetterEditorDockableModalComponent extends ModalBase<LetterEditorDockableModalData, boolean> implements OnInit {
	private letterEditorDockableModalService = inject(LetterEditorDockableModalService);
	private toasterService = inject(ToasterService);
	private fileService = inject(FileService);
	private taskManagementService = inject(TaskService);

	@ViewChild('modal')
	modal: DialogComponent;

	patientDocumentResponse: PatientDocumentResponse;
	documentContentControl = new FormControl<string | null>(null);
	maximized = true;

	_isLoading: boolean;
	froalaDataLoading = true;

	ngOnInit(): void {
		this.getData();
	}

	getData(): void {
		this.getPatientDocument().subscribe(response => {
			this.patientDocumentResponse = response;
			this.documentContentControl.setValue(response.documentContent);
			this.froalaDataLoading = false;
		});
	}

	@LoadingOverlayMethod()
	getPatientDocument(): Observable<PatientDocumentResponse> {
		return this.letterEditorDockableModalService.getPatientDocument(this.data.patientDocumentId);
	}

	closeModal(): void {
		this.dynamicModalRef.close(this.modal, null);
	}

	maximize(): void {
		this.modal.position = {X: 'right', Y: 'top'};
		this.modal.height = '93%';
		this.maximized = true;
	}

	minimize(): void {
		this.modal.position = {X: 'right', Y: 'bottom'};
		this.modal.height = 44;
		this.maximized = false;
	}

	updatePatientDocument(): void {
		const request = new PatientDocumentContentRequest();
		request.patientDocumentId = this.patientDocumentResponse.patientDocumentId;
		request.content = this.documentContentControl.value;
		this.letterEditorDockableModalService.updatePatientDocumentContent(request).subscribe(() => {
			this.saveTaskLogic();
		});
	}

	saveTaskLogic() {
		if (!this.data.existingTask) {
			void this.saveAsTask();
		} else {
			this.toasterService.showSavedSuccess();
		}
	}

	async saveAsTask(): Promise<void> {
		const result = await this.taskManagementService.openCreateTaskModal({
			patientId: this.patientDocumentResponse.patientId,
			categoryId: this.patientDocumentResponse.categoryId,
			notifyOnCompletion: true,
			notifyOnUpdate: true,
			patientDocumentId: this.patientDocumentResponse.patientDocumentId,
			disableCategorySelect: true,
			disablePatientSelect: true,
		});
		if (result) {
			this.closeModal();
		}
	}

	openPdfViewer(openPrint: boolean) {
		const request = new PatientDocumentContentRequest();
		request.patientDocumentId = this.patientDocumentResponse.patientDocumentId;
		request.content = this.documentContentControl.value;
		this.letterEditorDockableModalService.createPatientFileFromPatientDocument(request)
			.subscribe(patientFile => this.fileService.viewOrDownloadPatientFile(this.patientDocumentResponse.patientId, patientFile, openPrint));
	}
}
